<template>
  <div class="page-shell">
    <div v-if="errorMessage" class="error-banner">
      {{ errorMessage }}
    </div>

    <div v-if="isLoading" class="loading-row">
      <v-progress-circular indeterminate size="18" width="2" />
      <span>Loading association...</span>
    </div>

    <template v-else>
      <template v-if="company">
        <AppCard class="hero-card">
          <div class="hero-top">
            <div class="hero-icon" aria-hidden="true">
              <v-icon size="24" color="#e4572e">mdi-bank-outline</v-icon>
            </div>

            <div class="hero-copy">
              <div class="hero-badge-row">
                <!-- <span
                  v-if="applicationStatusLabel"
                  class="status-pill"
                  :class="statusPillClass"
                >
                  {{ applicationStatusLabel }}
                </span> -->
              </div>

              <div class="hero-title">{{ company.name }}</div>

              <!-- <div v-if="isVerified" class="hero-verified">
                <v-icon size="14" color="#e4572e">mdi-check-decagram</v-icon>
                <span>Verified Accrediting Body</span>
              </div> -->
              <div v-if="company.application_status !== 'not_applied'">
                <v-chip
                  :color="getStatusColor(company.application_status)"
                  class="text-capitalize"
                  label
                  >{{ company.application_status }}</v-chip
                >
              </div>
            </div>
          </div>

          <div class="hero-divider"></div>

          <div class="hero-stats">
            <div class="hero-stat">
              <div class="hero-stat__value">{{ membersCountLabel }}</div>
              <div class="hero-stat__label">Members</div>
            </div>
            <div class="hero-stat">
              <div class="hero-stat__value">
                <v-icon size="14" color="#f59e0b">mdi-star</v-icon>
                <span>{{ ratingLabel }}</span>
              </div>
              <div class="hero-stat__label">Rating</div>
            </div>
            <div class="hero-stat">
              <div class="hero-stat__value">{{ establishedLabel }}</div>
              <div class="hero-stat__label">Established</div>
            </div>
          </div>
        </AppCard>

        <div v-if="company.application_status === 'not_applied'">
          <AppButtonFlat block append-icon="mdi-arrow-right" @click="handleApply"
            >Apply Now</AppButtonFlat
          >
        </div>

        <AppCard class="section-card">
          <div class="section-row">
            <div class="section-icon" aria-hidden="true">
              <v-icon size="18" color="#64748b">mdi-map-marker-outline</v-icon>
            </div>
            <div class="section-copy">
              <div class="section-kicker">HEADQUARTERS</div>
              <div class="section-title">{{ headquartersLabel }}</div>
            </div>
          </div>
        </AppCard>

        <AppCard class="section-card">
          <div class="section-kicker">ABOUT</div>
          <div class="section-body">
            {{ aboutText }}
          </div>
        </AppCard>

        <AppCard class="section-card">
          <div class="section-head">
            <div class="section-head__icon" aria-hidden="true">
              <v-icon size="18" color="#e4572e">mdi-shield-check-outline</v-icon>
            </div>
            <div class="section-head__title">Member Benefits</div>
          </div>

          <div class="list-stack">
            <div
              v-for="(item, idx) in benefitItems"
              :key="`benefit-${idx}`"
              class="list-item"
            >
              <div class="list-bullet" aria-hidden="true">
                <v-icon size="14" color="#e4572e">mdi-check</v-icon>
              </div>
              <div class="list-text">{{ item }}</div>
            </div>
          </div>
        </AppCard>

        <AppCard class="section-card">
          <div class="section-head">
            <div class="section-head__icon" aria-hidden="true">
              <v-icon size="18" color="#e4572e">mdi-clipboard-text-outline</v-icon>
            </div>
            <div class="section-head__title">Eligibility Requirements</div>
          </div>

          <div class="list-stack">
            <div
              v-for="(item, idx) in eligibilityItems"
              :key="`elig-${idx}`"
              class="list-item"
            >
              <div class="list-number" aria-hidden="true">{{ idx + 1 }}</div>
              <div class="list-text">{{ item }}</div>
            </div>
          </div>
        </AppCard>
      </template>

      <AppCard v-else class="empty-card"> Association not found. </AppCard>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import { getCompanyDetails } from "@/modules/vendor/services/vendors.api";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { getStatusColor } from "@/shared/utils/utils";

type CompanyAddress = {
  city: string;
  state: string;
  distance: string;
};

type CompanyItem = {
  name: string;
  id: number;
  description: string;
  application_status?: string;
  application_id?: number | null;
  address: Partial<CompanyAddress> | null;
  verified?: boolean;
  is_verified?: boolean;
  members?: number;
  members_count?: number;
  rating?: number;
  avg_rating?: number;
  established?: number | string;
  established_year?: number | string;
  about?: string;
  benefits?: string[];
  eligibility_requirements?: string[];
  membership?: {
    monthly_price?: number | string;
    yearly_price?: number | string;
    yearly_save_label?: string;
  };
  [key: string]: any;
};

const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const errorMessage = ref("");
const company = ref<CompanyItem | null>(null);
const selectedPlan = ref<"monthly" | "yearly">("yearly");

function formatLocation(city?: string, state?: string) {
  const c = String(city || "").trim();
  const s = String(state || "").trim();
  if (c && s) return `${c}, ${s}`;
  return c || s || "—";
}

function formatCompactNumber(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return new Intl.NumberFormat(undefined).format(n);
}

function formatRating(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toFixed(1);
}

function formatPrice(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `$${Math.round(n)}`;
}

function getRouteCompanyId(): number | null {
  const raw = route.params.id;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

// Dummy placeholders (until backend provides these fields).
const DUMMY = {
  categoryLabel: "CHAMBER OF COMMERCE",
  isVerified: true,
  applicationStatusLabel: "Verified",
  statusPillClass: "status-pill--approved",
  membersCount: 4820,
  rating: 4.9,
  established: 1912,
  about:
    "This association represents members across the region. Members gain access to procurement opportunities, advocacy, and a vetted referral network.",
  benefits: [
    "Access to public & private procurement RFPs",
    "Legislative advocacy and policy representation",
    "Member directory & verified trust seal",
    "Quarterly networking summits",
    "Discounted group health & liability insurance",
  ],
  eligibility: [
    "Registered business entity (LLC, Inc., or sole proprietor)",
    "Minimum 1 year of operating history",
    "Two member references or industry endorsements",
  ],
  monthlyPrice: 49,
  yearlyPrice: 495,
  yearlySaveLabel: "Save 9%",
} as const;

const categoryLabel = computed(() => DUMMY.categoryLabel);
const isVerified = computed(() => DUMMY.isVerified);
const applicationStatusLabel = computed(() => DUMMY.applicationStatusLabel);
const statusPillClass = computed(() => DUMMY.statusPillClass);

const headquartersLabel = computed(() => {
  return formatLocation(company.value?.address?.city, company.value?.address?.state);
});

const aboutText = computed(() => {
  return DUMMY.about;
});

const membersCountLabel = computed(() => {
  return formatCompactNumber(DUMMY.membersCount);
});

const ratingLabel = computed(() => {
  return formatRating(DUMMY.rating);
});

const establishedLabel = computed(() => {
  return String(DUMMY.established);
});

const benefitItems = computed(() => {
  return [...DUMMY.benefits];
});

const eligibilityItems = computed(() => {
  return [...DUMMY.eligibility];
});

const monthlyPriceLabel = computed(() => formatPrice(DUMMY.monthlyPrice));
const yearlyPriceLabel = computed(() => formatPrice(DUMMY.yearlyPrice));
const yearlySaveLabel = computed(() => DUMMY.yearlySaveLabel);

async function loadCompany() {
  const id = getRouteCompanyId();
  if (!id) {
    company.value = null;
    errorMessage.value = "Invalid association id.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";
  try {
    const response: any = await getCompanyDetails(id);
    const payload = response?.data ?? response;
    company.value = payload as CompanyItem;
  } catch (error) {
    console.error(error);
    errorMessage.value = "Unable to load association. Please try again.";
    company.value = null;
  } finally {
    isLoading.value = false;
  }
}

function handleApply() {

  if(company.value?.approved){
    router.push({
      name: "vendor.apply-for-company",
      params: { company_id: getRouteCompanyId() },
    });
  } else {
    router.push({
      name: "vendor.apply-company-memberships",
      params: { companyId: getRouteCompanyId() },
    });
  }
}

onMounted(() => {
  void loadCompany();
});
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 10px !important;
  background: #f6f7f9;
  min-height: calc(100vh - 48px);
}

.loading-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--color-subtitle);
  font-size: 14px;
}

.error-banner {
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  border: 1px solid rgba(239, 68, 68, 0.18);
  padding: 10px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
}

.hero-card {
  padding: 16px;
  border-radius: 18px;
  background: #fff;
}

.hero-top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.hero-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(228, 87, 46, 0.1);
  border: 1px solid rgba(228, 87, 46, 0.18);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
}

.hero-copy {
  min-width: 0;
  flex: 1 1 auto;
}

.hero-badge-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  background: rgba(228, 87, 46, 0.1);
  color: #e4572e;
  font-weight: 800;
  font-size: 10px;
  letter-spacing: 0.08em;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.02em;
}

.status-pill--new {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

.status-pill--pending {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

.status-pill--approved {
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
}

.hero-title {
  margin-top: 8px;
  font-weight: 800;
  font-size: 20px;
  color: #0b1220;
  line-height: 1.15;
}

.hero-verified {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #e4572e;
  font-weight: 700;
  font-size: 12px;
}

.hero-divider {
  margin-top: 14px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.hero-stats {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.hero-stat {
  text-align: center;
}

.hero-stat__value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-weight: 900;
  font-size: 16px;
  color: #0b1220;
}

.hero-stat__label {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 800;
  color: rgba(15, 23, 42, 0.55);
  letter-spacing: 0.02em;
}

.section-card {
  padding: 14px;
  border-radius: 18px;
  background: #fff;
}

.section-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(100, 116, 139, 0.08);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
}

.section-copy {
  min-width: 0;
}

.section-kicker {
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  color: rgba(15, 23, 42, 0.55);
}

.section-title {
  margin-top: 2px;
  font-size: 15px;
  font-weight: 900;
  color: #0b1220;
}

.section-body {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.55;
  color: rgba(15, 23, 42, 0.72);
}

.section-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-head__icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(228, 87, 46, 0.1);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
}

.section-head__title {
  font-size: 15px;
  font-weight: 900;
  color: #0b1220;
}

.list-stack {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.list-item {
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 10px;
  align-items: start;
}

.list-bullet {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: rgba(228, 87, 46, 0.1);
  display: grid;
  place-items: center;
  margin-top: 1px;
}

.list-number {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 900;
  margin-top: 1px;
}

.list-text {
  font-size: 13px;
  line-height: 1.45;
  color: rgba(15, 23, 42, 0.78);
  font-weight: 600;
}

.plan-grid {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.plan-card {
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: #fff;
  border-radius: 14px;
  padding: 12px;
  text-align: left;
  cursor: pointer;
}

.plan-card--active {
  border-color: #e4572e;
  box-shadow: 0 0 0 1px rgba(228, 87, 46, 0.1);
}

.plan-kicker-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.plan-kicker {
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.06em;
  color: rgba(15, 23, 42, 0.65);
}

.plan-save {
  font-size: 10px;
  font-weight: 900;
  color: #15803d;
  background: rgba(34, 197, 94, 0.12);
  padding: 4px 8px;
  border-radius: 999px;
  white-space: nowrap;
}

.plan-price {
  margin-top: 8px;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.plan-amount {
  font-size: 22px;
  font-weight: 950;
  color: #0b1220;
}

.plan-unit {
  font-size: 12px;
  font-weight: 800;
  color: rgba(15, 23, 42, 0.6);
}

.plan-note {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.55);
}

.fineprint {
  margin-top: 12px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.6);
  font-weight: 600;
}

.actions-row {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}
</style>
